import { CollectionViewer, DataSource } from '@angular/cdk/collections';
import { FormGroup } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import {
  BehaviorSubject,
  combineLatest,
  merge,
  Observable,
  of,
  Subscription,
} from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';

export class ApiDataSource<T> implements DataSource<T> {
  _disableLoad = false;
  _formSubscription = Subscription.EMPTY;
  _loadSubscription = Subscription.EMPTY;
  _renderChangesSubscription = Subscription.EMPTY;
  _errorCallback?: () => void;
  private _filter = new BehaviorSubject<T | {}>({});
  private readonly _renderData = new BehaviorSubject<T[]>([]);

  constructor(
    public source: any,
    private _router: Router | null = null,
    private _route: ActivatedRoute | null = null,
    private _options: any = {},
  ) {
    this._updateChangeSubscription();
  }

  private _filterForm!: FormGroup;

  get filterForm(): FormGroup {
    return this._filterForm;
  }

  set filterForm(filterForm: FormGroup) {
    this._filterForm = filterForm;

    if (this._route?.snapshot?.queryParams[this._options.key + 'Filters']) {
      this._filterForm.patchValue(
        JSON.parse(
          this._route?.snapshot?.queryParams[this._options.key + 'Filters'],
        ),
      );
    }

    this._filter.next(this._filterForm.value);

    this._formSubscription.unsubscribe();
    this._formSubscription = this._filterForm.valueChanges.subscribe(
      (value) => {
        if (this._router && this._route) {
          this._router.navigate([], {
            relativeTo: this._route,
            queryParams: {
              [this._options.key + 'Filters']: JSON.stringify(value),
            },
            queryParamsHandling: 'merge', // remove to replace all query params by provided
          });
        }

        this._filter.next(value);
      },
    );
  }

  private _loading = false;

  get loading() {
    return this._loading;
  }

  get empty() {
    return !this.paginator?.length;
  }

  private _sort!: MatSort | null;

  get sort(): MatSort | null {
    return this._sort;
  }

  set sort(sort: MatSort | null) {
    if (
      sort &&
      this._route?.snapshot?.queryParams[this._options.key + 'Sort']
    ) {
      const _sort = JSON.parse(
        this._route?.snapshot?.queryParams[this._options.key + 'Sort'],
      );
      sort.direction = _sort.direction;
      sort.active = _sort.active;
      sort.start = _sort.start;
    }

    this._sort = sort;

    this._sort?.sortChange.subscribe(() => {
      if (this._router && this._route) {
        this._router.navigate([], {
          relativeTo: this._route,
          queryParams: {
            [this._options.key + 'Sort']: JSON.stringify({
              direction: sort?.direction,
              active: sort?.active,
              start: sort?.start,
            }),
          },
          queryParamsHandling: 'merge', // remove to replace all query params by provided
        });
      }
    });

    this._updateChangeSubscription();
  }

  private _paginator!: MatPaginator | null;

  get paginator(): MatPaginator | null {
    return this._paginator;
  }

  set paginator(paginator: MatPaginator | null) {
    this._paginator = paginator;
    this._updateChangeSubscription();
  }

  private _paginatorHeader!: MatPaginator | null;

  get paginatorHeader(): MatPaginator | null {
    return this._paginatorHeader;
  }

  set paginatorHeader(paginatorHeader: MatPaginator | null) {
    this._paginatorHeader = paginatorHeader;
    this._updateChangeSubscription();
  }

  filterFormatter = (value: T | {}) => value;

  clearFilter(form?: FormGroup, data: any = {}, empty = false) {
    if (empty) {
      this._disableLoad = true;
    }

    if (!form) {
      form = this.filterForm;
    }

    Object.keys(form.controls).forEach((k) => {
      const control = form?.get(k);
      if (control instanceof FormGroup) {
        this.clearFilter(control);
      }
    });
    this.filterForm.reset(data);

    if (empty) {
      this._renderData.next([]);
      this._disableLoad = false;
    }
  }

  refresh() {
    this.load();
  }

  connect() {
    return this._renderData;
  }

  disconnect(collectionViewer: CollectionViewer): void {}

  load(result?: any) {
    this._loadSubscription.unsubscribe();

    this._loading = true;

    let paginator = this.paginator;
    if (result && result[3]) {
      paginator = this.paginatorHeader;
    }

    this._loadSubscription = this.source({
      ...(this._filter ? this.filterFormatter(this._filter.value) : {}),
      sort: this.sort
        ? [
            [
              this.sort.active,
              this.sort.direction ? this.sort.direction : this.sort.start,
            ],
          ]
        : null,
      offset: (paginator?.pageSize || 0) * (paginator?.pageIndex || 0),
      size: paginator?.pageSize,
    }).subscribe(
      (result: any) => {
        this._loading = false;
        if (this.paginator) {
          this.paginator.length = result.meta?.total;
        }
        if (this.paginatorHeader) {
          this.paginatorHeader.length = result.meta?.total;
        }
        this._renderData.next(result.data);
      },
      () => {
        if (this._errorCallback) {
          this._errorCallback();
        }
      },
    );
  }

  _updateChangeSubscription() {
    const sortChange: Observable<Sort | null | void> = this._sort
      ? (merge(
          this._sort.sortChange,
          this._sort.initialized,
        ) as Observable<Sort | void>)
      : of(null);

    const pageChange: Observable<PageEvent | null | void> = this._paginator
      ? (merge(this._paginator.page, this._paginator.initialized) as any)
      : of(null);
    const pageChangeHeader: Observable<PageEvent | null | void> = this
      ._paginatorHeader
      ? (merge(
          this._paginatorHeader.page,
          this._paginatorHeader.initialized,
        ) as any)
      : of(null);

    this._renderChangesSubscription.unsubscribe();
    this._renderChangesSubscription = combineLatest([
      this._filter,
      sortChange,
      pageChange,
      pageChangeHeader,
    ])
      .pipe(
        filter(() => !this._disableLoad),
        debounceTime(300),
      )
      .subscribe((result) => {
        this.load(result);
      });
  }
}
